import { nextTick, ref, type Ref } from "vue";
import { ApolloQueryResult } from "@apollo/client/core";
import { generateApolloClient } from "@/common/apolloClientUtil";
import { getShopList } from "@/common/graphql/shop";
import {
  localStorageGetChainCode,
  localStorageGetCustomerCode,
  localStorageGetGroup,
} from "@/common/localStorageUtil";
import {
  ListDeliveryAreaDocument,
  ListDeliveryAreaQuery,
  ListDeliveryAreaQueryVariables,
  ShopListInput,
} from "@/generated/graphql";

type ChainItem = { __typename?: "Chain"; chain_code: string; name: string };
type ShopItem = { __typename?: "ShopResponse"; shop_code: string; name: string };
type AreaItem = {
  __typename?: "DeliveryArea";
  area_code: string;
  area_name: string;
  area_name_kana: string;
};

type ChainArray = Array<ChainItem | null | undefined> | null | undefined;
type ShopArray = Array<ShopItem | null | undefined> | null | undefined;
type AreaArray = Array<AreaItem | null | undefined> | null | undefined;

/**
 * 画面ロジックを扱う composable。
 */
export const useDeliveryRequestFilterState = () => {
  const chainListItems = ref<ChainArray>([]);
  const bufChainListItems = ref<ChainArray>([]);
  const chainSelectValues = ref<ChainArray>([]);
  const bufChainSelectValues = ref<ChainArray>([]);
  const chainAllCheckFlag = ref("all");
  const searchConditionChainCode = ref<(string | null | undefined)[] | null | undefined>([]);
  const searchConditionShopCode = ref<(string | null | undefined)[] | null | undefined>([]);
  const areaListItems = ref<AreaArray>(null);
  const areaSelectValues = ref<{ area_code: string; area_name: string } | null>(null);
  const shopSelectValues = ref<{ shop_code: string; name: string }[]>([]);
  const shopAllCheckFlag = ref("none");
  const shopListItems = ref<ShopArray>([]);
  const bufShopListItems = ref<ShopArray>([]);

  return {
    chainListItems,
    bufChainListItems,
    chainSelectValues,
    bufChainSelectValues,
    chainAllCheckFlag,
    searchConditionChainCode,
    searchConditionShopCode,
    areaListItems,
    areaSelectValues,
    shopSelectValues,
    shopAllCheckFlag,
    shopListItems,
    bufShopListItems,
  };
};

/**
 * composableへ渡す依存をまとめた型。
 */
interface UseDeliveryRequestFilterOptionsParams {
  chainListItems: Ref<ChainArray>;
  bufChainListItems: Ref<ChainArray>;
  chainSelectValues: Ref<ChainArray>;
  bufChainSelectValues: Ref<ChainArray>;
  chainAllCheckFlag: Ref<string>;
  searchConditionChainCode: Ref<(string | null | undefined)[] | null | undefined>;
  searchConditionShopCode: Ref<(string | null | undefined)[] | null | undefined>;
  areaListItems: Ref<AreaArray>;
  areaSelectValues: Ref<{ area_code: string; area_name: string } | null>;
  shopSelectValues: Ref<{ shop_code: string; name: string }[]>;
  shopAllCheckFlag: Ref<string>;
  shopListItems: Ref<ShopArray>;
  bufShopListItems: Ref<ShopArray>;
  listDataArray: Ref<unknown[]>;
  searchShopSelectValues: Ref<string[]>;
  listOptions: Ref<{ page: number }>;
  listDataTotalCount: Ref<number>;
  chainViewFlag: boolean;
  authType: Ref<string>;
  errdialog: Ref<boolean>;
  errMessage: Ref<string>;
}

/**
 * 画面ロジックを扱う composable。
 */
export const useDeliveryRequestFilterOptions = ({
  chainListItems,
  bufChainListItems,
  chainSelectValues,
  bufChainSelectValues,
  chainAllCheckFlag,
  searchConditionChainCode,
  searchConditionShopCode,
  areaListItems,
  areaSelectValues,
  shopSelectValues,
  shopAllCheckFlag,
  shopListItems,
  bufShopListItems,
  listDataArray,
  searchShopSelectValues,
  listOptions,
  listDataTotalCount,
  chainViewFlag,
  authType,
  errdialog,
  errMessage,
}: UseDeliveryRequestFilterOptionsParams) => {
  const shopViewFlag = localStorageGetGroup();
  const shopDisabledFlag = ref(true);
  const areaDisabledFlag = ref(true);

  const shopListInput: ShopListInput = {
    chain_code: localStorageGetChainCode(),
    customer_code: localStorageGetCustomerCode(),
    limit: 10000,
    offset: 0,
  };

  const onchangeChainAllCheck = async (): Promise<void> => {
    if (chainAllCheckFlag.value === "all" || chainAllCheckFlag.value === "some") {
      chainSelectValues.value = [] as unknown as ChainArray;
      searchConditionChainCode.value = [];

      shopSelectValues.value = [];
      shopListItems.value = [];
      listDataArray.value = [];
      searchShopSelectValues.value = [];

      chainAllCheckFlag.value = "none";
      shopAllCheckFlag.value = "none";
      shopDisabledFlag.value = true;
      areaSelectValues.value = null;
      areaListItems.value = [];
      areaDisabledFlag.value = true;
    } else {
      chainAllCheckFlag.value = "all";
      bufChainListItems.value?.forEach((chainValue) => {
        if (!chainValue) {
          return;
        }
        chainSelectValues.value?.push({
          chain_code: chainValue.chain_code,
          name: chainValue.name,
        });
        (searchConditionChainCode.value as string[]).push(chainValue.chain_code);
      });

      areaSelectValues.value = null;
      areaListItems.value = [];
      areaDisabledFlag.value = true;
    }

    await nextTick();
  };

  const onChangeChain = async (): Promise<void> => {
    try {
      shopSelectValues.value = [];
      shopListItems.value = [];
      listDataArray.value = [];
      searchConditionShopCode.value = [];
      areaSelectValues.value = null;
      areaListItems.value = [] as unknown as AreaArray;
      areaDisabledFlag.value = true;

      listOptions.value.page = 1;
      listDataTotalCount.value = 0;

      if (!chainSelectValues.value) {
        return;
      }

      searchConditionChainCode.value = [];
      bufChainSelectValues.value = chainSelectValues.value;

      chainSelectValues.value = [] as unknown as ChainArray;
      searchConditionChainCode.value = [];
      chainAllCheckFlag.value = "none";

      if (bufChainSelectValues.value?.length === chainListItems.value?.length) {
        chainAllCheckFlag.value = "all";
      } else if ((bufChainSelectValues.value?.length ?? 0) > 0) {
        chainAllCheckFlag.value = "some";
      } else {
        chainAllCheckFlag.value = "none";
      }

      await nextTick();

      chainListItems.value?.forEach((chainValue) => {
        bufChainSelectValues.value?.forEach((chainSelectValue) => {
          if (!chainValue || !chainSelectValue) {
            return;
          }
          if (chainValue.chain_code === chainSelectValue.chain_code) {
            chainSelectValues.value?.push({
              chain_code: chainValue.chain_code,
              name: chainValue.name,
            });
            (searchConditionChainCode.value as string[]).push(chainValue.chain_code);
          }
        });
      });
      bufChainSelectValues.value = [] as unknown as ChainArray;

      if ((chainSelectValues.value?.length ?? 0) === 1) {
        shopListInput.chain_code = chainSelectValues.value?.[0]?.chain_code ?? "";
        bufShopListItems.value = await getShopList(shopListInput);

        shopSelectValues.value = [];
        searchConditionShopCode.value = [];
        shopAllCheckFlag.value = "none";

        if ((bufShopListItems.value?.length ?? 0) > 0) {
          shopListItems.value = [];
          bufShopListItems.value?.forEach((shopValue) => {
            if (!shopValue) {
              return;
            }
            shopListItems.value?.push({
              shop_code: shopValue.shop_code,
              name: shopValue.name,
            });
            // チェーン単体選択時は関連する全店舗を自動選択する
            shopSelectValues.value.push({
              shop_code: shopValue.shop_code,
              name: shopValue.name,
            });
            (searchConditionShopCode.value as string[]).push(shopValue.shop_code);
          });
          shopAllCheckFlag.value = "all";
          shopDisabledFlag.value = false;
          areaDisabledFlag.value = authType.value === "0" ? false : true;
        } else {
          shopListItems.value = [];
          shopDisabledFlag.value = true;
          areaDisabledFlag.value = true;
        }
      } else {
        shopAllCheckFlag.value = "none";
        shopDisabledFlag.value = true;
        shopSelectValues.value = [];
        searchConditionShopCode.value = [];
        shopListItems.value = [];
        areaDisabledFlag.value = true;
      }
    } catch (err: unknown) {
      if (err instanceof Error) {
        errdialog.value = true;
        errMessage.value = err.message;
        return;
      }
      errdialog.value = true;
      errMessage.value = "エラーが発生しました。";
    }
  };

  const onchangeShopAllCheck = async (): Promise<void> => {
    if (shopDisabledFlag.value === true) {
      return;
    }
    if (shopAllCheckFlag.value === "all" || shopAllCheckFlag.value === "some") {
      shopSelectValues.value = [];
      searchConditionShopCode.value = [];
      shopAllCheckFlag.value = "none";
      areaSelectValues.value = null;
      areaListItems.value = [];
      areaDisabledFlag.value = true;
    } else {
      shopAllCheckFlag.value = "all";
      bufShopListItems.value?.forEach((shopValue) => {
        if (!shopValue) {
          return;
        }
        shopSelectValues.value.push({
          shop_code: shopValue.shop_code,
          name: shopValue.name,
        });
        (searchConditionShopCode.value as string[]).push(shopValue.shop_code);
      });
      areaSelectValues.value = null;
      areaListItems.value = [];
      areaDisabledFlag.value = true;
    }
    await nextTick();
  };

  const onChangeShop = async (): Promise<void> => {
    try {
      areaSelectValues.value = null;
      areaListItems.value = [] as unknown as AreaArray;
      areaDisabledFlag.value = true;

      if (!shopSelectValues.value) {
        return;
      }

      searchConditionShopCode.value = [];
      const bufShopSelectValuesLocal = shopSelectValues.value;

      shopSelectValues.value = [];
      searchConditionShopCode.value = [];
      shopAllCheckFlag.value = "none";

      if (bufShopSelectValuesLocal.length === (shopListItems.value?.length ?? 0)) {
        shopAllCheckFlag.value = "all";
      } else if (bufShopSelectValuesLocal.length > 0) {
        shopAllCheckFlag.value = "some";
      } else {
        shopAllCheckFlag.value = "none";
      }

      await nextTick();

      shopListItems.value?.forEach((shopValue) => {
        bufShopSelectValuesLocal.forEach((shopSelectValue) => {
          if (!shopValue) {
            return;
          }
          if (shopValue.shop_code === shopSelectValue.shop_code) {
            shopSelectValues.value.push({
              shop_code: shopValue.shop_code,
              name: shopValue.name,
            });
            (searchConditionShopCode.value as string[]).push(shopValue.shop_code);
          }
        });
      });

      if (shopSelectValues.value.length === 1) {
        const chainCode = chainViewFlag
          ? (chainSelectValues.value?.[0]?.chain_code ?? "")
          : localStorageGetChainCode();

        const apolloClient = await generateApolloClient();
        const listArea: ApolloQueryResult<ListDeliveryAreaQuery> =
          await apolloClient.query<
            ListDeliveryAreaQuery,
            ListDeliveryAreaQueryVariables
          >({
            query: ListDeliveryAreaDocument,
            variables: {
              chain_code: chainCode,
              customer_code: localStorageGetCustomerCode(),
              shop_code: shopSelectValues.value[0].shop_code,
            },
          });

        areaListItems.value = listArea.data.listDeliveryArea;
        areaDisabledFlag.value = false;

        if (shopSelectValues.value.length !== 1) {
          areaSelectValues.value = null;
          areaListItems.value = [] as unknown as AreaArray;
          areaDisabledFlag.value = true;
        } else {
          areaDisabledFlag.value = false;
        }
      }
    } catch (err: unknown) {
      if (err instanceof Error) {
        errdialog.value = true;
        errMessage.value = err.message;
        return;
      }
      errdialog.value = true;
      errMessage.value = "エラーが発生しました。";
    }
  };

  return {
    shopViewFlag,
    shopDisabledFlag,
    areaDisabledFlag,
    shopListInput,
    onchangeChainAllCheck,
    onChangeChain,
    onchangeShopAllCheck,
    onChangeShop,
  };
};
